import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OPPORTUNITY_STATES, type OpportunityState } from '@tennis-scanner/shared';
import { X } from 'lucide-react';

// OpportunityListFilterSchema sortBy values — keep in sync with the contract.
export const SORT_OPTIONS = [
  { value: 'rankScore', label: 'Prioridad' },
  { value: 'currentEdgePct', label: 'Mejor edge' },
  { value: 'firstDetectedAt', label: 'Más recientes' },
] as const;
export type SortKey = (typeof SORT_OPTIONS)[number]['value'];

// "Active states" = the default operator view on the dashboard. Excludes
// terminal states (executed / passed / closed / expired) — those live in
// Alerts History (V2). Snoozed is included so the operator can un-snooze.
export const ACTIVE_STATES: OpportunityState[] = [
  'detected',
  'active',
  'weakened',
  'reappeared',
  'snoozed',
];

export interface DashboardFilters {
  states: OpportunityState[];
  sortBy: SortKey;
}

interface Props {
  filters: DashboardFilters;
  onChange: (next: DashboardFilters) => void;
  totalCount: number;
}

const STATE_OPTIONS: OpportunityState[] = [...OPPORTUNITY_STATES];

export function DashboardFilters({ filters, onChange, totalCount }: Props) {
  function toggleState(state: OpportunityState): void {
    const next = filters.states.includes(state)
      ? filters.states.filter((s) => s !== state)
      : [...filters.states, state];
    onChange({ ...filters, states: next });
  }

  function reset(): void {
    onChange({ states: ACTIVE_STATES, sortBy: 'rankScore' });
  }

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border bg-card px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
          Estados
        </span>
        <div className="flex flex-wrap gap-1">
          {STATE_OPTIONS.map((state) => {
            const active = filters.states.includes(state);
            return (
              <Badge
                key={state}
                variant={active ? 'default' : 'outline'}
                className="cursor-pointer select-none capitalize"
                onClick={() => toggleState(state)}
              >
                {state}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
          Ordenar
        </span>
        <Select
          value={filters.sortBy}
          onValueChange={(v) => onChange({ ...filters, sortBy: v as SortKey })}
        >
          <SelectTrigger className="h-8 w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="ml-auto flex items-center gap-3">
        <span className="font-mono text-xs text-muted-foreground">
          {totalCount} opp{totalCount === 1 ? '' : 's'}
        </span>
        <Button variant="ghost" size="sm" onClick={reset}>
          <X className="size-3" /> Reset
        </Button>
      </div>
    </div>
  );
}
